/**
 * Context, tipi e funzioni del tema (F-070).
 *
 * Separati dal provider (`ThemeProvider.tsx`) per la stessa ragione di
 * `auth-context.ts`: un modulo che esporta sia componenti sia funzioni rompe
 * il Fast Refresh di Vite.
 *
 * `auto` non è un terzo tema, è una delega: significa "quello che dice il
 * sistema operativo", e va rivalutata ogni volta che il sistema cambia idea —
 * chi tiene il telefono in modalità notturna a orario se ne accorge la sera.
 * Per questo la scelta dell'utente (tre valori) e il tema effettivo (due) sono
 * due cose diverse e stanno in due variabili diverse.
 */

import { createContext, useContext } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

/** Stessa chiave usata dallo script in `index.html`: cambiarla qui non basta. */
export const THEME_STORAGE_KEY = 'labinvestment.theme';

export const DARK_QUERY = '(prefers-color-scheme: dark)';

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'auto';
}

export function readStoredChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isChoice(stored) ? stored : 'auto';
  } catch {
    // localStorage può lanciare (modalità privata, cookie di terze parti
    // bloccati): la preferenza si perde, l'applicazione no.
    return 'auto';
  }
}

export function storeChoice(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Vedi readStoredChoice: la scelta vale per questa sessione e basta.
  }
}

/** `matchMedia` manca in jsdom: lì il sistema conta come chiaro. */
export function systemTheme(): ResolvedTheme {
  if (typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Applica il tema al documento.
 *
 * `data-theme` porta **sempre** un valore risolto, mai `auto`: così
 * `tokens.css` ha un solo selettore per il tema scuro invece di doverne tenere
 * due allineati (uno per la media query di sistema, uno per la scelta manuale).
 *
 * Il colore della barra del browser lo legge da `--color-bg` invece di
 * ripeterlo: è lo stesso colore, e due copie divergono al primo ritocco.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.dataset.theme = resolved;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const background = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-bg')
    .trim();
  if (background) meta.setAttribute('content', background);
}

export type ThemeContextValue = {
  /** Cosa ha scelto l'utente: è questo che va segnato nel selettore. */
  choice: ThemeChoice;
  /** Cosa si vede davvero: `auto` già risolto. */
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
};

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve essere usato dentro <ThemeProvider>');
  }
  return context;
}
